"use client";

import { useCallback, useEffect, useRef } from "react";

import { useLomdaV3Editor } from "../store/useLomdaV3Editor";
import { isTypingTarget } from "../lib/isTypingTarget";

// A new block's text editors mount a moment after the block itself, so the
// block is measured only once its size has held still this long.
const SETTLE_MS = 120;
const MAX_WAIT_MS = 1000;

// The author scrolled, clicked or used a key elsewhere while it waited.
const USER_ACTS = ["wheel", "touchmove", "pointerdown", "keydown"] as const;

const scrollParent = (el: HTMLElement) => {
    for (let p = el.parentElement; p; p = p.parentElement)
        if (
            p.scrollHeight > p.clientHeight &&
            /auto|scroll/.test(getComputedStyle(p).overflowY)
        )
            return p;
    return null;
};

const fullyVisible = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const box = scrollParent(el)?.getBoundingClientRect() ?? {
        top: 0,
        bottom: window.innerHeight,
    };
    return r.top >= box.top && r.bottom <= box.bottom;
};

/** Scrolls this block into view when `revealBlock` targets it. Returns a ref
 *  for the block's root. */
export function useRevealBlock(blockId: string) {
    const node = useRef<HTMLElement | null>(null);
    const reveal = useLomdaV3Editor((s) => s.revealBlockId === blockId);
    const clearReveal = useLomdaV3Editor((s) => s.clearReveal);

    useEffect(() => {
        const el = node.current;
        if (!reveal || !el) return;
        let settle = setTimeout(go, SETTLE_MS);
        const cap = setTimeout(go, MAX_WAIT_MS);
        const ro = new ResizeObserver(() => {
            clearTimeout(settle);
            settle = setTimeout(go, SETTLE_MS);
        });
        ro.observe(el);
        const cancel = (e: Event) => {
            if (e.type === "keydown" && isTypingTarget(e.target)) return;
            stop();
            clearReveal();
        };
        for (const type of USER_ACTS)
            window.addEventListener(type, cancel, {
                capture: true,
                passive: true,
            });
        function go() {
            stop();
            clearReveal();
            if (!el || fullyVisible(el)) return;
            // A tall block shows from its top instead of from its middle.
            const tall = el.offsetHeight > window.innerHeight / 2;
            el.scrollIntoView({
                behavior: "smooth",
                block: tall ? "start" : "center",
            });
        }
        function stop() {
            for (const type of USER_ACTS)
                window.removeEventListener(type, cancel, { capture: true });
            ro.disconnect();
            clearTimeout(settle);
            clearTimeout(cap);
        }
        return stop;
    }, [reveal, clearReveal]);

    return useCallback((el: HTMLElement | null) => {
        node.current = el;
    }, []);
}
